import * as Sentry from '@sentry/react';

/**
 * Centralized error tracking utility for 502 and 504 gateway errors
 * This helps track API timeouts and gateway issues across the application
 */

/**
 * Track gateway errors (502/504) with comprehensive context
 * @param {Object} error - The error object
 * @param {Object} context - Additional context information
 * @param {Function} getState - Optional Redux getState function to extract user context
 */
export function trackGatewayError(error, context = {}, getState = null) {
  if (!error || (error.status !== 502 && error.status !== 504)) {
    return;
  }

  const errorContext = {
    status: error.status,
    statusText:
      error.statusText ||
      (error.status === 502 ? 'Bad Gateway' : 'Gateway Timeout'),
    url: error.url || context.url || 'Unknown URL',
    method: error.method || context.method || 'Unknown Method',
    currentPage:
      typeof window !== 'undefined'
        ? window.location.href
        : 'Server-side request',
    userAgent:
      typeof navigator !== 'undefined'
        ? navigator.userAgent
        : 'Server-side request',
    timestamp: new Date().toISOString(),
    errorMessage: error.message || 'No error message',
    responseText:
      error.response?.text ||
      error.text ||
      context.responseText ||
      'No response text',
    errorSource: context.source || 'unknown',
    requestParams: context.requestParams || {},
    requestData: context.requestData || 'No request data',
    ...context,
  };

  // Add user context if getState function is provided (from Redux)
  if (getState && typeof getState === 'function') {
    try {
      const state = getState();
      if (state.auth && state.auth.user) {
        errorContext.userId = state.auth.user.id;
        errorContext.username = state.auth.user.username;
        errorContext.userPlan = state.auth.user.pro ? 'Pro' : 'Free';
      }
    } catch (stateError) {
      console.warn(
        'Could not extract user context from Redux state:',
        stateError
      );
    }
  }

  // Create a descriptive error message
  const errorMessage = `${error.status} Gateway Error: ${errorContext.statusText}`;

  Sentry.captureException(new Error(errorMessage), {
    tags: {
      errorType: 'gateway_error',
      statusCode: error.status.toString(),
      page:
        typeof window !== 'undefined'
          ? window.location.pathname
          : 'server-side',
      source: context.source || 'unknown',
      endpoint: context.endpoint || errorContext.url,
    },
    extra: errorContext,
    level: 'error',
  });

  // Also log to console for development debugging
  console.error('Gateway Error Tracked:', errorMessage, errorContext);
}

/**
 * Setup global error handlers to catch unhandled 502/504 errors
 */
export function setupGlobalErrorTracking() {
  if (typeof window === 'undefined') {
    return; // Skip on server-side
  }

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    if (error && (error.status === 502 || error.status === 504)) {
      trackGatewayError(error, {
        source: 'unhandled_promise_rejection',
        errorMessage: 'Unhandled promise rejection with gateway error',
      });
    }
  });

  // Handle global errors
  window.addEventListener('error', (event) => {
    const error = event.error;
    if (error && (error.status === 502 || error.status === 504)) {
      trackGatewayError(error, {
        source: 'global_error_handler',
        errorMessage: 'Global error handler caught gateway error',
      });
    }
  });
}
